import React, { useEffect, useMemo, useState } from 'react';
import { useEvent, useUnit } from 'effector-react';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { UiLabel } from '@nx-frontend/shared/ui/label';
import { UiSelect, UiSelectItem } from '@nx-frontend/shared/ui/select';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { MenuItem, MenuItemType, UiTooltipPosition } from '@nx-frontend/shared/types';
import { CreatePortalEntityType } from './create-portal-entity';
import {
  $originSelectMenuItems,
  $originSelectPortalId,
  $originSelectPortals,
  fetchOriginSelectPortalsFx,
  setSelectedPortalIdEvent,
} from './store/origin-select-store';

const PageTypes = [MenuItemType.NOTE, MenuItemType.PORTAL_PAGE];
const FolderTypes = [MenuItemType.NOTES_FOLDER, MenuItemType.PORTAL_SECTION];

export function OriginSelect({
  type,
  changeProjectionMenuItem,
}: {
  type: CreatePortalEntityType;
  changeProjectionMenuItem: (value: MenuItem | null) => void;
}) {
  const [currentOrganization, portals, selectedPortalId, menuItems] = useUnit([
    $currentOrganization,
    $originSelectPortals,
    $originSelectPortalId,
    $originSelectMenuItems,
  ]);
  const [fetchOriginSelectPortals, setSelectedPortalId] = useEvent([
    fetchOriginSelectPortalsFx,
    setSelectedPortalIdEvent,
  ]);
  const [selectedMenuItemId, setSelectedMenuItemId] = useState<string | undefined>(undefined);
  const portalOptions: UiSelectItem[] = portals
    .map((portal) => {
      const name = (portal.settings as unknown as { name: string }).name;
      return {
        value: portal.globalId,
        label: name,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

  const menuItemOptions = useMemo(() => {
    const types = type === CreatePortalEntityType.PAGE ? PageTypes : CreatePortalEntityType.FOLDER ? FolderTypes : [];
    return menuItems
      .filter((item) => types.includes(item.type) && item.permissions?.allowAll && !item.originGlobalId)
      .map((item) => {
        return {
          value: item.id,
          label: item.name,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [menuItems, type]);

  useEffect(() => {
    if (selectedMenuItemId) {
      const menuItem = menuItems.find((item) => item.id === selectedMenuItemId);
      changeProjectionMenuItem(menuItem ?? null);
    } else {
      changeProjectionMenuItem(null);
    }
  }, [changeProjectionMenuItem, menuItems, selectedMenuItemId]);

  useEffect(() => {
    fetchOriginSelectPortals({ orgId: currentOrganization.globalId });
  }, [currentOrganization.globalId, fetchOriginSelectPortals]);

  const handleSelectPortal = (value: string) => {
    setSelectedPortalId(value);
  };

  const handleSelectMenuItem = (value: string) => {
    setSelectedMenuItemId(value);
  };

  const menuItemSelectLabel = useMemo(() => {
    if (type === CreatePortalEntityType.PAGE) {
      return 'Page to link';
    } else if (type === CreatePortalEntityType.FOLDER) {
      return 'Folder to link';
    } else if (type === CreatePortalEntityType.PROCESS) {
      return 'Process to link';
    }
    return '';
  }, [type]);

  const infoMessage = useMemo(() => {
    if (!portalOptions.length) {
      return (
        <>
          No available portals to create linked pages or folders. At least <br /> two portals are required to proceed
        </>
      );
    }
    return (
      <>
        If source pages contain forms or File Upload blocks, the <br /> submitted data and files will be uploaded to the
        target portal, not <br /> the original one.
      </>
    );
  }, [portalOptions.length]);

  return (
    <>
      <div className="border-l-2 border-sky-500 px-3 py-2 bg-sky-50 rounded-r-md">{infoMessage}</div>
      <div className="flex flex-col">
        <UiLabel title="Existing portal" type="small" />
        <UiSelect<string>
          options={portalOptions}
          value={selectedPortalId}
          nullable={true}
          disabled={!portalOptions.length}
          className={['max-w-none']}
          onChange={handleSelectPortal}
        ></UiSelect>
      </div>
      <div className="flex flex-col">
        <div className="flex gap-1 items-center">
          <UiLabel title={menuItemSelectLabel} type="small" />
          <TippyTooltip
            message={
              <span>
                Processes and entities with permissions <br /> are excluded from the list for projection <br />{' '}
                creation. Only entities without permissions <br /> will appear here
              </span>
            }
            position={UiTooltipPosition.TOP}
            show={true}
            interactive={false}
          >
            <div className="mb-1">
              <Icon icon={IconsMap.QUESTION} className="w-4 h-4 text-neutral-500" />
            </div>
          </TippyTooltip>
        </div>
        <UiSelect<string>
          options={menuItemOptions}
          value={selectedMenuItemId}
          nullable={true}
          disabled={!selectedPortalId || !menuItemOptions?.length || !portalOptions.length}
          className={['max-w-none']}
          onChange={handleSelectMenuItem}
        ></UiSelect>
      </div>
    </>
  );
}
